import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { NotistackServices, type PublicTeamDTO, type PublicTeamMemberDTO, type PublicWorkspaceDTO, WorkspaceServices } from "shared/services";
import { HandleError } from "shared/utils";

interface TeamPageProviderProps {
	workspace: PublicWorkspaceDTO | null;
	teams: PublicTeamDTO[];
	loadingTeams: boolean;
	children: React.ReactNode;
}

interface TeamPageContextValue {
	workspace: PublicWorkspaceDTO | null;
	loadingTeams: boolean;
	selectedTeamId: string | null;
	selectedTeam: PublicTeamDTO | null;
	members: PublicTeamMemberDTO[];
	loadingMembers: boolean;
	teamNotFound: boolean;
	reloadMembers: () => Promise<void>;
}

const TeamPageContext = React.createContext<TeamPageContextValue | null>(null);

export const TeamPageProvider: React.FC<TeamPageProviderProps> = ({ workspace, teams, loadingTeams, children }) => {
	const { teamId } = useParams<{ teamId: string }>();
	const selectedTeamId = teamId ?? null;
	const selectedTeam = useMemo(() => teams.find((team) => String(team.id) === selectedTeamId) ?? null, [teams, selectedTeamId]);

	const [members, setMembers] = useState<PublicTeamMemberDTO[]>([]);
	const [loadingMembers, setLoadingMembers] = useState(false);

	const reloadMembers = useCallback(async () => {
		if (!workspace || !selectedTeamId || !selectedTeam) {
			setMembers([]);
			setLoadingMembers(false);
			return;
		}

		setLoadingMembers(true);
		try {
			const membersData = await WorkspaceServices.getMembers(String(workspace.id), selectedTeamId);
			setMembers(membersData);
		} catch (error) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar membros");
		} finally {
			setLoadingMembers(false);
		}
	}, [workspace, selectedTeamId, selectedTeam]);

	useEffect(() => {
		if (!workspace || !selectedTeamId || !selectedTeam) {
			setMembers([]);
			setLoadingMembers(false);
			return;
		}

		let cancelled = false;
		setMembers([]);
		setLoadingMembers(true);

		const loadMembers = async () => {
			try {
				const membersData = await WorkspaceServices.getMembers(String(workspace.id), selectedTeamId);
				if (cancelled) return;
				setMembers(membersData);
			} catch (error) {
				if (!cancelled) {
					NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar membros");
				}
			} finally {
				if (!cancelled) {
					setLoadingMembers(false);
				}
			}
		};

		void loadMembers();
		return () => {
			cancelled = true;
		};
	}, [workspace, selectedTeamId, selectedTeam]);

	const teamNotFound = !loadingTeams && selectedTeamId != null && teams.length > 0 && !selectedTeam;

	const contextValue = useMemo<TeamPageContextValue>(
		() => ({
			workspace,
			loadingTeams,
			selectedTeamId,
			selectedTeam,
			members,
			loadingMembers,
			teamNotFound,
			reloadMembers,
		}),
		[workspace, loadingTeams, selectedTeamId, selectedTeam, members, loadingMembers, teamNotFound, reloadMembers],
	);

	return <TeamPageContext.Provider value={contextValue}>{children}</TeamPageContext.Provider>;
};

export const useTeamPageContext = (): TeamPageContextValue => {
	const context = React.useContext(TeamPageContext);
	if (!context) {
		throw new Error("useTeamPageContext deve ser usado dentro de TeamPageProvider");
	}

	return context;
};
